import React, { useState, useEffect, useMemo } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import { Select, MenuItem, FormControl, InputLabel, TextField, Grid, CircularProgress, InputAdornment } from "@mui/material";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

import DataTable from "examples/Tables/DataTable";
import jsPDF from "jspdf";
import "jspdf-autotable";
import MDButton from "components/MDButton";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import { disableBackNavigation } from "core/utils/storage";
import api from "core/interceptors/api";
import moment from "moment";
import Swal from "sweetalert2"; // Import Swal for consistent alerts

// Icons for UI enhancement
import AssessmentIcon from '@mui/icons-material/Assessment'; // Report icon
import EventIcon from '@mui/icons-material/Event'; // Date icon
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'; // Export PDF icon
import SearchIcon from '@mui/icons-material/Search'; // Generate Report icon
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // No data warning icon

function AdminReport() {
    const [selectedOption, setSelectedOption] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false); // New loading state for data fetching
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);

    const navigate = useNavigate();

    // Define columns dynamically based on selectedOption
    const columns = useMemo(() => {
        if (selectedOption === 'vendorsByDate') {
            return [
                { Header: "S.No", accessor: "serial_no", align: "center" },
                { Header: "Business Name", accessor: "business_name" },
                { Header: "Owner Name", accessor: "owner_name" },
                { Header: "Email", accessor: "email" },
                { Header: "Mobile Number", accessor: "mobile_number" },
                { Header: "Business Type", accessor: "business_type" },
                { Header: "Business Category", accessor: "business_category" },
                { Header: "Created At", accessor: "created_at" },
                { Header: "Approved Status", accessor: "approved_status" },
                { Header: "Status", accessor: "status" },
            ];
        } else if (selectedOption === 'serviceProvidersByDate') {
            return [
                { Header: "S.No", accessor: "serial_no", align: "center" },
                { Header: "Service Provider Name", accessor: "service_provider_name" }, // This should likely be owner_name based on backend model
                { Header: "Owner Name", accessor: "owner_name" },
                { Header: "Email", accessor: "email" },
                { Header: "Mobile Number", accessor: "mobile_number" },
                { Header: "Service Type", accessor: "services_type" }, // Corrected to match model `services_type`
                { Header: "Created At", accessor: "created_at" },
                { Header: "Approved Status", accessor: "approved_status" },
                { Header: "Status", accessor: "status" },
            ];
        } else if (selectedOption === 'customersByDate') {
            return [
                { Header: "S.No", accessor: "serial_no", align: "center" },
                { Header: "Customer ID", accessor: "customer_id" },
                { Header: "Name", accessor: "name" },
                { Header: "Email", accessor: "email" },
                { Header: "Phone Number", accessor: "phone_number" },
                { Header: "Location", accessor: "location" },
                { Header: "Current Location", accessor: "current_location" },
                { Header: "Role", accessor: "role" },
                { Header: "Status", accessor: "status" },
                { Header: "Is Verified", accessor: "is_verified" },
                { Header: "Created At", accessor: "created_at" },
            ];
        }
        return [];
    }, [selectedOption]);

    const paginatedRows = useMemo(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return reportData.slice(startIndex, endIndex);
    }, [reportData, page, rowsPerPage]);

    useEffect(() => {
        disableBackNavigation();
    }, []);

    // Date validation logic
    useEffect(() => {
        let newDateErrors = { startDate: '', endDate: '' };
        if (startDate && endDate) {
            const startMoment = moment(startDate).startOf('day');
            const endMoment = moment(endDate).startOf('day');

            if (endMoment.isBefore(startMoment)) {
                newDateErrors.endDate = "End Date cannot be before Start Date.";
            }
        }
        setDateErrors(newDateErrors);
    }, [startDate, endDate]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const fetchReportData = async (url, start, end, type) => {
        setLoading(true); // Start loading before API call
        setReportData([]); // Clear previous data
        setPage(0); // Reset page

        try {
            const response = await api.get(url, {
                params: {
                    start_date: moment(start).format('YYYY-MM-DD'),
                    end_date: moment(end).format('YYYY-MM-DD')
                }
            });

            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const mappedRows = response.data.map((item, index) => {
                    const base = { serial_no: index + 1, created_at: moment(item.created_at).isValid() ? moment(item.created_at).format('YYYY-MM-DD HH:mm:ss') : 'N/A' };
                    if (type === 'vendorsByDate') {
                        return {
                            ...base,
                            business_name: item.business_name || 'N/A',
                            owner_name: item.owner_name || 'N/A',
                            email: item.email || 'N/A',
                            mobile_number: item.mobile_number || 'N/A',
                            business_type: item.business_type || 'N/A',
                            business_category: item.business_category || 'N/A',
                            approved_status: item.approved_status || 'N/A',
                            status: item.status || 'N/A',
                        };
                    } else if (type === 'serviceProvidersByDate') {
                        return {
                            ...base,
                            // Mismatched name fixed: service_provider_name often maps to owner_name
                            service_provider_name: item.owner_name || 'N/A', // Assuming `owner_name` is the display name
                            owner_name: item.owner_name || 'N/A',
                            email: item.email || 'N/A',
                            mobile_number: item.mobile_number || 'N/A',
                            services_type: item.services_type || 'N/A', // Use `services_type` from model
                           
                            approved_status: item.approved_status || 'N/A',
                            status: item.status || 'N/A',
                            
                        };
                    } else if (type === 'customersByDate') {
                        return {
                            ...base,
                            customer_id: item.customer_id || 'N/A',
                            name: item.name || 'N/A',
                            email: item.email || 'N/A',
                            phone_number: item.phone_number || 'N/A',
                            location: item.location || 'N/A',
                            current_location: item.current_location || 'N/A',
                            role: item.role || 'N/A',
                            status: item.status || 'N/A',
                            is_verified: typeof item.is_verified === 'boolean' ? (item.is_verified ? "Yes" : "No") : 'N/A',
                        };
                    }
                    return item; // Fallback
                });
                setReportData(mappedRows);
            } else {
                setReportData([]);
                Swal.fire({
                    icon: 'info',
                    title: 'No Data Found',
                    text: 'No data matches the selected criteria for this report type.',
                });
            }
        } catch (error) {
            console.error(`Error fetching ${type} for report:`, error);
            setReportData([]);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Failed to fetch ${type} data. Please try again.`,
            });
        } finally {
            setLoading(false); // End loading after API call
        }
    };

    const handleChange = (event) => {
        setSelectedOption(event.target.value);
        setStartDate(null);
        setEndDate(null);
        setReportData([]);
        setPage(0);
        setDateErrors({ startDate: '', endDate: '' }); // Clear date errors
    };

    const handleGenerateReport = () => {
        if (!selectedOption) {
            Swal.fire('Error', 'Please select a report type.', 'error');
            return;
        }
        if (!startDate || !endDate) {
            Swal.fire('Error', 'Please select both a start date and an end date.', 'error');
            return;
        }
        if (dateErrors.startDate || dateErrors.endDate) {
            Swal.fire('Error', 'Please correct the date errors before generating the report.', 'error');
            return;
        }

        const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
        const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

        if (selectedOption === 'vendorsByDate') {
            fetchReportData('/reports/vendors/created_between_dates', formattedStartDate, formattedEndDate, 'vendorsByDate');
        } else if (selectedOption === 'serviceProvidersByDate') {
            fetchReportData('/reports/service_providers/created_between_dates', formattedStartDate, formattedEndDate, 'serviceProvidersByDate');
        } else if (selectedOption === 'customersByDate') {
            fetchReportData('/reports/customers/added_between_dates', formattedStartDate, formattedEndDate, 'customersByDate');
        }
    };

    const handleExportPDF = () => {
        if (reportData.length === 0) {
            Swal.fire('Info', 'No data to export. Please generate a report first.', 'info');
            return;
        }

        const doc = new jsPDF('l', 'mm', 'a4');
        let reportTitle = "";
        let fileName = "";
        let tableHeaders = [];
        let tableBody = [];

        if (selectedOption === 'vendorsByDate') {
            reportTitle = "Vendors Created Between Dates Report";
            fileName = `vendors-created-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}.pdf`;
            tableHeaders = columns.map(col => col.Header);
            tableBody = reportData.map(row => columns.map(col => row[col.accessor]));
        } else if (selectedOption === 'serviceProvidersByDate') {
            reportTitle = "Service Providers Created Between Dates Report";
            fileName = `service-providers-created-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}.pdf`;
            tableHeaders = columns.map(col => col.Header);
            tableBody = reportData.map(row => columns.map(col => row[col.accessor]));
        } else if (selectedOption === 'customersByDate') {
            reportTitle = "Customers Added Between Dates Report";
            fileName = `customers-added-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}.pdf`;
            tableHeaders = columns.map(col => col.Header);
            tableBody = reportData.map(row => columns.map(col => row[col.accessor]));
        }

        doc.text(reportTitle, 14, 15);
        doc.autoTable({
            theme: "striped",
            head: [tableHeaders],
            body: tableBody,
            startY: 20,
        });
        doc.save(fileName);
    };

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton onLogout={() => navigate("/login")} />
            </MDBox>
            <DashboardNavbar />
            <MDBox pt={4} pb={3} px={3}>
                <Card>
                    <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info" display="flex" alignItems="center" gap={1}>
                        <AssessmentIcon sx={{ color: 'white' }} />
                        <MDTypography variant="h6" color="white">Admin Reports</MDTypography>
                    </MDBox>
                    <MDBox p={3}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Grid container spacing={3} alignItems="flex-start"> {/* Align items to top */}
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>Select Report Type</InputLabel>
                                        <Select
                                            label="Select Report Type"
                                            value={selectedOption}
                                            onChange={handleChange}
                                            sx={{ height: '45px' }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AssessmentIcon color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        >
                                            <MenuItem value="">-- Select Report Type --</MenuItem>
                                            <MenuItem value="vendorsByDate">Vendors Created Between Dates</MenuItem>
                                            <MenuItem value="serviceProvidersByDate">Service Providers Created Between Dates</MenuItem>
                                            <MenuItem value="customersByDate">Customers Added Between Dates</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {selectedOption && (
                                    <>
                                        <Grid item xs={12} md={4}>
                                            <DatePicker
                                                label="Start Date"
                                                value={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        variant="outlined"
                                                        error={!!dateErrors.startDate}
                                                        helperText={dateErrors.startDate}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <EventIcon color="action" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                                maxDate={endDate || new Date()} // Prevent start date being after end date or in future
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DatePicker
                                                label="End Date"
                                                value={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        variant="outlined"
                                                        error={!!dateErrors.endDate}
                                                        helperText={dateErrors.endDate}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <EventIcon color="action" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                                minDate={startDate} // End date cannot be before start date
                                                maxDate={new Date()} // Prevent end date from being in the future
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={12} display="flex" gap={2} justifyContent="flex-start"> {/* Buttons row */}
                                    <MDButton
                                        variant="gradient"
                                        color="info" // Changed to info color for consistency
                                        onClick={handleGenerateReport}
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                        disabled={!selectedOption || !startDate || !endDate || !!dateErrors.startDate || !!dateErrors.endDate || loading}
                                    >
                                        {loading ? "Generating..." : "Generate Report"}
                                    </MDButton>
                                    {reportData.length > 0 && (
                                        <MDButton
                                            onClick={handleExportPDF}
                                            variant="gradient"
                                            color="error"
                                            startIcon={<CloudDownloadIcon />}
                                        >
                                            Export as PDF
                                        </MDButton>
                                    )}
                                </Grid>
                            </Grid>
                        </LocalizationProvider>

                        {/* Report Data Table Section */}
                        <MDBox pt={4}> {/* Added padding top for separation */}
                            {loading && reportData.length === 0 ? ( // Show loader only if data is empty and loading
                                <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                                    <CircularProgress color="info" />
                                    <MDTypography variant="h6" color="text" ml={2}>Fetching report data...</MDTypography>
                                </MDBox>
                            ) : reportData.length > 0 ? (
                                <DataTable
                                    table={{ columns: columns, rows: paginatedRows }}
                                    isSorted={true}
                                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                                    showTotalEntries={true}
                                    canSearch={false} 
                                    noEndBorder
                                    pagination={{
                                        variant: "contained",
                                        color: "dark",
                                        page: page,
                                        onPageChange: handlePageChange,
                                        rowsPerPage: rowsPerPage,
                                        onRowsPerPageChange: handleRowsPerPageChange,
                                        count: reportData.length,
                                    }}
                                />
                            ) : (
                                selectedOption && (startDate || endDate) && !loading && (
                                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px">
                                        <WarningAmberIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
                                        <MDTypography variant="h6" color="textSecondary" align="center">
                                            No data found for the selected criteria.
                                        </MDTypography>
                                        <MDTypography variant="body2" color="textSecondary" align="center" mt={1}>
                                            Try adjusting your dates or report type.
                                        </MDTypography>
                                    </MDBox>
                                )
                            )}
                        </MDBox>
                    </MDBox>
                </Card>
            </MDBox>
        </DashboardLayout>
    );
}

export default AdminReport;